import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useLogin, useMe } from "@/api/hooks";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { useI18n } from "@/i18n";
import { cx } from "@/lib/cx";
import { currentSegment } from "@/lib/time";

/** Eight stone courses, one per 3-hour window; the current window is marked. */
function Courses() {
  const { fmt } = useI18n();
  const current = currentSegment();
  const day = new Date();
  return (
    <ol className="flex h-full flex-col" aria-hidden>
      {Array.from({ length: 8 }, (_, i) => {
        return (
          <li
            key={i}
            className={cx(
              "flex flex-1 items-center justify-between px-8 text-sm",
              i % 2 ? "bg-rail-2" : "bg-rail",
              i === current &&
                "shadow-[inset_4px_0_0_var(--c-accent)] rtl:shadow-[inset_-4px_0_0_var(--c-accent)]",
            )}
          >
            <span
              className={cx(
                "num",
                i === current ? "font-medium text-rail-text" : "text-rail-muted",
              )}
            >
              {String(i * 3).padStart(2, "0")}:00
            </span>
            {i === current && <span className="text-xs text-rail-muted">{fmt.longDate(day)}</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function LoginPage() {
  const { t, lang, setLang } = useI18n();
  const { data: me } = useMe();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  if (me) return <Navigate to="/" replace />;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    login.mutate(
      { username, password },
      {
        onSuccess: () =>
          navigate((location.state as { from?: string } | null)?.from ?? "/", { replace: true }),
      },
    );
  };

  const error =
    login.error instanceof ApiError ? login.error.message : login.error ? t("login.failed") : "";

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="hidden lg:block">
        <Courses />
      </div>
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="text-sm text-muted hover:text-text"
            lang={lang === "ar" ? "en" : "ar"}
          >
            {lang === "ar" ? "English" : "العربية"}
          </button>
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          <BrandMark quadrant={currentSegment() % 4} size={64} />
          <h1 className="mt-5 text-3xl font-semibold tracking-[-0.015em]">{t("brand.name")}</h1>
          <p className="mt-2 text-muted">{t("login.subtitle")}</p>
          <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
            <Field label={t("login.username")}>
              {(props) => (
                <input
                  {...props}
                  className="field-input"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                />
              )}
            </Field>
            <Field label={t("login.password")} error={error || undefined}>
              {(props) => (
                <input
                  {...props}
                  className="field-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              )}
            </Field>
            <Button type="submit" variant="primary" loading={login.isPending} className="mt-2">
              {t("login.submit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
